import { inject, Injectable } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { environment } from '@client/environments/environment';
import {
    EarlyAccessFeatureConfig,
    EarlyAccessFeatureKey,
    EarlyAccessProgramConfig,
} from '@monsido/services/early-access/early-access.types';

export const evaluateFeatureEnabled = (
    featureConfig: EarlyAccessFeatureConfig | undefined,
    isTestCustomer: boolean,
    isPreviewCustomer: boolean,
): boolean => {
    if (!featureConfig) {
        return false;
    }

    if (featureConfig.includes('all')) {
        return true;
    }

    if (isTestCustomer && isPreviewCustomer) {
        return featureConfig.includes('preview');
    }

    if (isTestCustomer) {
        return featureConfig.includes('test');
    }

    if (isPreviewCustomer) {
        return featureConfig.includes('preview');
    }

    return false;
};

@Injectable({
    providedIn: 'root',
})
export class EarlyAccessService {
    private readonly sessionService: SessionService = inject(SessionService);

    isResolved (): boolean {
        return this.sessionService.me !== undefined;
    }

    isFeatureEnabled (feature: EarlyAccessFeatureKey): boolean {
        if (!this.isResolved()) {
            throw new Error('EAP audience evaluation attempted before user state resolved');
        }

        const featureConfig = this.getFeatureConfig(feature);
        return evaluateFeatureEnabled(featureConfig, this.isTestCustomer(), this.isPreviewCustomer());
    }

    hasFeatureConfig (feature: EarlyAccessFeatureKey): boolean {
        const config = environment.earlyAccessProgram as EarlyAccessProgramConfig | undefined;
        return Boolean(config) && Object.prototype.hasOwnProperty.call(config, feature);
    }

    private getFeatureConfig (feature: EarlyAccessFeatureKey): EarlyAccessFeatureConfig | undefined {
        const config = environment.earlyAccessProgram as EarlyAccessProgramConfig | undefined;
        return config?.[feature];
    }

    private isPreviewCustomer (): boolean {
        return this.sessionService.me?.customer?.subscription?.preview_customer === true;
    }

    private isTestCustomer (): boolean {
        return this.sessionService.me?.customer?.subscription?.test_customer === true;
    }
}
